import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import ApiClientManager from 'core/apiClient';
import { exchangeToken, fetchMe, signOut } from 'thunk';
import debounce from 'lodash/debounce';
import { AUTH_WATCHDOG_TIMEOUT_MS, RedirectKeys } from '@constants';
import { canAutoRedirectToLogin, recordAutoLoginRedirect } from 'utils/loginRedirectLoopGuard';
import { fetchTenants } from 'TenantModule/thunks';
import useSandboxes, { SandboxIsoStateIds } from 'SandboxModule/hooks/useSandboxes';
import { useMount, useSearchParam } from 'react-use';
import { Realm } from 'types';
import { selectAuthUser } from 'selectors';
import { Tenant } from 'TenantModule/types';
import { notificationService } from 'services/notificationService';

type _CustomApiBuilderParams = Nullable<{
  realmId: Realm['realm_id'] | undefined;
  tenantName: Tenant['tenantName'] | undefined;
}>;

export const useAuthenticationLayer = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectAuthUser);
  const { fetchSandboxes } = useSandboxes();
  const accessToken = useSearchParam(RedirectKeys.AccessToken);
  // Remove when switch tenant is working
  // const realmIdParam = localStorage.getItem(
  //   LocalStorageKeys.LastSelectedRealmId
  // );
  // const tenantNameParam = localStorage.getItem(
  //   LocalStorageKeys.LastSelectedTenantName
  // );

  /* -------------------------------------------------------------------------- */

  /**
   * Any failure in the token-exchange chain lands here. AuthenticationLayer
   * then auto-redirects to login (loop-guarded) instead of leaving the user
   * stuck on the "Authenticating" screen (GLX-1743).
   */
  const [isAuthError, setIsAuthError] = useState(false);

  const handleAuthFailure = useCallback(() => {
    setIsAuthError(true);
  }, []);

  const completeAuthFlowV2 = useCallback(
    ({ providedAuthToken }: { providedAuthToken: string }) => {
      dispatch(
        fetchMe({
          meta: {
            providedAuthToken,
            onFailure: handleAuthFailure,
            onSuccess: ({ response }) => {
              const { tenantNameToUse, realmIdToUse } =
                response.tenantName && response.tenantInfo
                  ? {
                      tenantNameToUse: response.tenantName,
                      realmIdToUse: response.tenantInfo.primaryRealm!,
                    }
                  : {
                      // Fail gracefully?
                      tenantNameToUse: ApiClientManager.getDefaultTenant().tenantName,
                      realmIdToUse: ApiClientManager.getDefaultRealm().realm_id,
                    };

              dispatch(
                // authorization is a requirement
                fetchTenants({
                  meta: {
                    tenantName: tenantNameToUse,
                    realmId: realmIdToUse,
                    onFailure: () => {
                      // ✅ FIX: Replace alert() with toast notification
                      notificationService.error('Failed to complete authentication');
                    },
                    onSuccess: ({ response }) => {
                      /**
                       * Find the selected tenant from the response by name
                       * Make sure tenant exist
                       */
                      const tenantFromResponse =
                        tenantNameToUse &&
                        response.find((tenant) => tenant.tenantName === tenantNameToUse);

                      const tenantToStore =
                        tenantFromResponse || ApiClientManager.getDefaultTenant();

                      const isRealmIdExistOnTenant = tenantToStore.realms.find(
                        (realmId) => realmId === realmIdToUse,
                      );

                      if (isRealmIdExistOnTenant) {
                        fetchSandboxes(SandboxIsoStateIds.AppbarRealmSelector, {
                          meta: {
                            // ...customApiBuilderParams,
                            tenantName: tenantNameToUse,
                            realmId: realmIdToUse,
                            onSuccess: ({ response }) => {
                              const realmToStore =
                                response.sandbox_info.find(
                                  (realm) => realm.realm_id === realmIdToUse,
                                ) || ApiClientManager.getDefaultRealm();

                              ApiClientManager.setTenant(tenantToStore);
                              ApiClientManager.setRailsSandbox(realmToStore);
                            },
                          },
                        });
                      }
                    },
                  },
                }),
              );
            },
          },
        }),
      );
    },
    [dispatch, fetchSandboxes, handleAuthFailure],
  );
  // useMemo (not useCallback) because the memoized value is debounce()'s return
  // value, not an inline function; useCallback requires an inline function literal.
  const handleUnauthenticated = useMemo(
    () =>
      debounce(() => {
        if (!accessToken) return;

        dispatch(
          exchangeToken({
            tempToken: accessToken,
            meta: {
              tenantName: ApiClientManager.getDefaultTenant().tenantName,
              realmId: ApiClientManager.getDefaultRealm().realm_id,
              onFailure: handleAuthFailure,
              onSuccess: ({ response }) => {
                completeAuthFlowV2({
                  providedAuthToken: response.auth_token,
                });
              },
            },
          }),
        );
      }, 400),
    [dispatch, accessToken, completeAuthFlowV2, handleAuthFailure],
  );

  // Cancel a pending debounced exchange on unmount so it cannot dispatch
  // during teardown (e.g. after SIGN_OUT started the redirect to login)
  useEffect(() => {
    return () => {
      handleUnauthenticated.cancel();
    };
  }, [handleUnauthenticated]);

  useMount(() => {
    if (user?.auth_token && user?.account) {
      // User is already authenticated, no action needed
    } else if (accessToken) {
      // Has access token in param BUT auth token Does Not exist
      handleUnauthenticated();
    } else if (canAutoRedirectToLogin()) {
      // No token to exchange and no persisted session — there is nothing to
      // wait for, so bounce straight to login instead of rendering the
      // "Authenticating" screen forever (GLX-1743)
      recordAutoLoginRedirect();
      dispatch(signOut());
    } else {
      handleAuthFailure();
    }
  });

  const isAuthenticated = !!user?.account;

  // Failsafe: if the auth flow silently stalls (a hung request or a failure
  // path not surfaced via onFailure), fail over to the error screen rather
  // than spinning forever (GLX-1743)
  useEffect(() => {
    if (isAuthenticated || isAuthError) return;

    const watchdog = setTimeout(handleAuthFailure, AUTH_WATCHDOG_TIMEOUT_MS);
    return () => clearTimeout(watchdog);
  }, [isAuthenticated, isAuthError, handleAuthFailure]);

  // A completed authentication always wins over a late or spurious auth
  // error (e.g. the watchdog firing just as auth completes, or fetchMe
  // failing after the token exchange already established the session) —
  // never tear down a working session; mid-session errors have their own
  // handling (401 toast/modal)
  if (isAuthenticated) {
    return {
      isLoading: false,
      isError: false,
      canMoveToNextLayer: true,
    };
  }

  if (isAuthError) {
    return {
      isLoading: false,
      isError: true,
      canMoveToNextLayer: false,
    };
  }

  return {
    isLoading: true,
    isError: false,
    canMoveToNextLayer: false,
  };
};
